import React, { Suspense, useEffect, useState } from "react";
import { Routes, Route, BrowserRouter, Navigate } from "react-router-dom";
import jwt_decode from "jwt-decode";
import setAuthToken from "./utils/setAuthToken";
import { currentUser, getProfile, loginUserViaWallet, attachWallet, setAuthChecked } from "./reducers/authReducer";

import axios from "axios";
import { API_URL } from "./actions/constant";
import Loader from "./components/common/Spinner";
import FullPageLoader from "./components/common/FullPageLoader";

import { useSelector } from "react-redux";
import "./App.css";
import "./ee-global-polish.css";
import "./ee-professional-theme.css";
import "./ee-profile-redesign.css";
import "./ee-notify-theme.css";
import "./ee-design-tokens.css";
import "./ee-modern-ui.css";
import "./ee-motion.css";
import "./ee-property-card-glass.css";
import "./ee-home.css";
import "./ee-ui.css";
import MotionLayer from "./components/commonlayout/MotionLayer";
import PrivateRoute from "./components/common/PrivateRoute";
import AdminRoute from "./components/admin/AdminRoute";
import FacilityManagerRoute from "./components/facilitymanager/FacilityManagerRoute";
import { LoadingProvider } from "./utils/loadingContext";
import ScrollToTop from "./components/common/ScrollToTop";
import { getSetting } from "./reducers/platformReducer";
import store from "./store";
import { onWalletConnect, web3Modal } from "./components/web3modal";
import { wasSolanaConnected, onSolanaAccountChanged } from "./lib/solanaWallet";
import { setWeb3, setUnsupportedChain, clearWeb3 } from "./reducers/web3Reducer";
import { toChainId, getChain, isSupportedChain, setActiveChainId, getActiveChainId, subscribeActiveChain } from "./config/chains";
import { signWalletLoginChallenge, runWalletAuthOnce } from "./utils/walletAuth";
import { initNotify, Notify } from "./utils/notify";
import DialogHost from "./components/common/ui/DialogHost";

axios.defaults.baseURL = API_URL;

// Tell the backend which network every API call is for (the backend's
// chainContext middleware reads x-chain-id) and keep it in sync when the user
// switches network. See backend/config/chains.js.
axios.defaults.headers.common["x-chain-id"] = String(getActiveChainId());
subscribeActiveChain((id) => {
  axios.defaults.headers.common["x-chain-id"] = String(id);
});
// One-time, app-wide toast styling — see utils/notify.js (single import path,
// standard timings, tokens-driven colours).
initNotify();

const Landing = React.lazy(() => import("./components/layouts/Landing"));
const Login = React.lazy(() => import("./components/auth/Login"));
const VerifyOtp = React.lazy(() => import("./components/auth/VerifyOtp"));
const AdminKyc = React.lazy(() => import("./components/admin/kyc/AdminKyc"));
const AdminProperties = React.lazy(() => import("./components/admin/properties/AdminProperties"));
const AdminEscrow = React.lazy(() => import("./components/admin/escrow/AdminEscrow"));
const AdminPayouts = React.lazy(() => import("./components/admin/payouts/AdminPayouts"));
const AdminLayout = React.lazy(() => import("./components/admin/layout/AdminLayout"));
const AdminFacilityManagers = React.lazy(() => import("./components/admin/facilitymanagers/AdminFacilityManagers"));
const AdminRates = React.lazy(() => import("./components/admin/rates/AdminRates"));
const Register = React.lazy(() => import("./components/auth/Register"));
const VerifyAccount = React.lazy(() =>
  import("./components/auth/VerifyAccount")
);
// "Add email login" for wallet-first accounts — Option B of the dual
// login-method work: lets an existing wallet account add email+password+OTP
// on top, without creating a second account. See attach-email route in
// backend/routes/userRoutes.js.
const AttachEmailLogin = React.lazy(() =>
  import("./components/auth/AttachEmailLogin")
);

const ForgotPassword = React.lazy(() =>
  import("./components/auth/ForgotPassword")
);
const ResetPassword = React.lazy(() =>
  import("./components/auth/ResetPassword")
);

const Terms = React.lazy(() =>
  import("./components/user/termsandconditions/Terms")
);
const Cookies = React.lazy(() =>
  import("./components/user/termsandconditions/Cookies")
);
const PrivacyPolicy = React.lazy(() =>
  import("./components/user/termsandconditions/Privacypolicy")
);
const GeneralRisks = React.lazy(() =>
  import("./components/user/termsandconditions/GeneralRisks")
);
const Property = React.lazy(() => import("./components/properties/Property"));
const PropertyDetail = React.lazy(() =>
  import("./components/properties/PropertyDetail")
);
const AboutPage = React.lazy(() => import("./components/about/AboutPage"));
const Contact = React.lazy(() => import("./components/contact/Contact"));
const Dashboard = React.lazy(() =>
  import("./components/user/dashboard/Dashboard")
);
const TokenDistributionPage = React.lazy(() =>
  import("./components/user/token/TokenDistributionPage")
);
const TokenDistributionDetailPage = React.lazy(() =>
  import("./components/user/token/TokenDistributionDetailPage")
);
const TokenHoldingsPage = React.lazy(() =>
  import("./components/user/token/TokenHoldingsPage")
);
const Marketplace = React.lazy(() =>
  import("./components/user/marketplace/Marketplace")
);
const Governance = React.lazy(() =>
  import("./components/user/governance/Governance")
);
const Spv = React.lazy(() => import("./components/user/spv/Spv"));
const AddSpv = React.lazy(() => import("./components/user/spv/AddSpv"));
const Kyc = React.lazy(() => import("./components/user/kyc/Kyc"));
const Wishlist = React.lazy(() =>
  import("./components/user/wishlist/Wishlist")
);
const MyProperty = React.lazy(() =>
  import("./components/user/myproperty/MyProperty")
);
const CreateProperty = React.lazy(() =>
  import("./components/user/addproperty/CreateProperty")
);
const EditProperty = React.lazy(() =>
  import("./components/user/editproperty/EditProperty")
);
const Buyers = React.lazy(() => import("./components/user/buyers/Buyers"));
const DigitalCertificate = React.lazy(() =>
  import("./components/user/buyers/DigitalCertificate")
);
const MyPurchases = React.lazy(() =>
  import("./components/user/purchases/MyPurchases")
);
const Profile = React.lazy(() => import("./components/user/account/Profile"));
const UpdatePassword = React.lazy(() =>
  import("./components/user/account/UpdatePassword")
);
const Logout = React.lazy(() => import("./components/user/logout/Logout"));
const FacilityManagerLayout = React.lazy(() => import("./components/facilitymanager/FacilityManagerLayout"));
const FacilityManagerPayouts = React.lazy(() => import("./components/facilitymanager/FacilityManagerPayouts"));
const PackageDetail = React.lazy(() =>
  import("./components/user/account/PackageDetails")
);
const ConfirmProfile = React.lazy(() =>
  import("./components/user/account/ConfirmProfile")
);
const ComingSoon = React.lazy(() =>
  import("./components/comingsoon/ComingSoon")
);
const ValuationPartners = React.lazy(() =>
  import("./components/user/valuation/ValuationPartners")
);
const FullyFunded = React.lazy(() =>
  import("./components/user/fullyfunded/FullyFunded")
);
const Refunds = React.lazy(() => import("./components/user/refunds/Refunds"));
const UserGuide = React.lazy(() =>
  import("./components/user/documentation/UserGuide")
);
store.dispatch(getSetting());

export default function App() {
  const { connected } = useSelector((state) => state.web3);
  const authState = useSelector((state) => state.auth);
  const isAuthenticated = authState && authState.isAuthenticated;
  const authLoading = authState && authState.loading;

  // Force light theme always
  useEffect(() => {
    if (typeof window !== "undefined") {
      const root = document.documentElement;
      root.setAttribute("data-eeap-theme", "light");
      try {
        window.localStorage.setItem("eeap-theme", "light");
      } catch (e) {}
      document.body.classList.remove("eeap-dark-header");
      document.body.classList.add("eeap-light-header");
    }
  }, []);

  // ---- NEW: gate routes until we restore auth from localStorage
  const [bootstrapped, setBootstrapped] = useState(false);

  useEffect(() => {
    const initAuth = async () => {
      try {
        const token = localStorage.getItem("jwtToken");
        if (token) {
          setAuthToken(token);
          const decoded = jwt_decode(token);

          // check expiry
          const currentTime = Date.now() / 1000;
          if (decoded.exp && decoded.exp < currentTime) {
            // token expired: clean up
            localStorage.removeItem("jwtToken");
          } else {
            // set user in redux and fetch profile
            store.dispatch(currentUser(decoded));
            await store.dispatch(getProfile());
          }
        }
      } catch (e) {
        // if anything goes wrong, just start unauthenticated
        localStorage.removeItem("jwtToken");
      } finally {
        setBootstrapped(true);
        // Fires exactly once per app load, after the JWT (if any) has been
        // restored/validated and its profile fetched. PrivateRoute /
        // AdminRoute / FacilityManagerRoute gate their initial render on
        // this instead of `loading`/`profileloading` — those two flip on
        // and off for a dozen unrelated things (attaching an email,
        // verifying an OTP, connecting a wallet...) that have nothing to
        // do with "has the app finished figuring out who's logged in", and
        // gating route rendering on them was unmounting whichever page
        // (most visibly Complete Your Profile mid-OTP-entry) the user was
        // on every time one of those fired.
        store.dispatch(setAuthChecked(true));
      }
    };

    initAuth();
  }, []);

  // Email-session users never need this component's own background
  // "reconnect a wallet automatically" behavior — deliberately
  // connecting/attaching a wallet for them happens through explicit
  // buttons elsewhere instead (EditProfile.js's "Connect Wallet",
  // Detail.js's own wallet check on the investment page, which talks to
  // window.ethereum directly and never touches web3Modal). Letting this
  // run for them means every page load/refresh, or the instant
  // MetaMask's active account changes for ANY reason, can pop the
  // wallet-picker modal (MetaMask / XDC Pay / WalletConnect) on top of
  // whatever the user is doing — including right on top of the
  // investment page's own "wrong wallet" warning. Checked from the JWT
  // alone (not the redux profile, which may not have finished loading
  // yet) so this can never race the profile fetch.
  const isEmailSessionActive = () => {
    const token = localStorage.getItem("jwtToken");
    if (!token) return false;
    try {
      const decoded = jwt_decode(token);
      const stillValid = decoded.exp && decoded.exp > Date.now() / 1000;
      // authMethod, not "!decoded.walletAddress" — see the matching note
      // in onConnect() above for why walletAddress alone can't tell an
      // email session apart from a wallet session.
      return !!(stillValid && decoded.authMethod !== 'wallet');
    } catch (e) {
      return false;
    }
  };

  // Keep your wallet auto-reconnect as-is
  useEffect(() => {
    if ((web3Modal.cachedProvider || wasSolanaConnected()) && !connected && !isEmailSessionActive()) {
      onConnect();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onConnect = async () => {
    // No forceAccountPicker here deliberately — this only runs when
    // web3Modal.cachedProvider is still set (i.e. the user never logged
    // out), so this is silently restoring an already-approved connection
    // on page load. Forcing the picker here would mean every page refresh
    // shows a wallet permission popup for an already-connected user. The
    // explicit "Connect Wallet" button click (web3modal/index.js) is the
    // one that passes forceAccountPicker: true.
    const web3data = await onWalletConnect();
    if (web3data) {
      store.dispatch(setWeb3(web3data));

      // If we already have a valid, unexpired session (restored from
      // localStorage above), this is just reconnecting the wallet's
      // provider/signer for an already-logged-in user — no need to make
      // them re-sign a message just to reload the page. Only go through
      // the sign-in challenge when actually establishing a new session.
      const existingToken = localStorage.getItem("jwtToken");
      // True when there IS a currently-valid session but it's NOT wallet-
      // based (i.e. logged in via email) — this is what the bug report
      // describes: an email user reconnects/switches their wallet and this
      // effect (fired via web3Modal.cachedProvider on mount, or via the
      // accountsChanged listener below — which fires as a side effect of
      // the ordinary "Connect Wallet" click, racing ConnectButton's own
      // handler in web3modal/index.js) used to call loginUserViaWallet
      // unconditionally, silently swapping their email session out for a
      // brand-new/different wallet account. An email session here should
      // get the wallet ATTACHED to it instead, never replaced.
      let isExistingEmailSession = false;
      if (existingToken) {
        try {
          const decoded = jwt_decode(existingToken);
          const stillValid = decoded.exp && decoded.exp > Date.now() / 1000;
          // One wallet per chain family on an account: EVM in walletAddress, Solana in solanaWalletAddress.
          const isSolanaWallet = web3data.family === "solana";
          const sessionWallet = isSolanaWallet ? decoded.solanaWalletAddress : decoded.walletAddress;
          const sameWallet =
            !!sessionWallet &&
            (isSolanaWallet
              ? sessionWallet === web3data.address
              : sessionWallet.toLowerCase() === web3data.address.toLowerCase());
          if (stillValid && sameWallet) return;
          // A live session that has no wallet of THIS family yet (e.g. a MetaMask account connecting
          // Phantom) gets the wallet ATTACHED, never swapped for a different account.
          // authMethod (added to the JWT in backend/utils/token.js), not
          // "!decoded.walletAddress" — an email-login user who has
          // separately registered/attached a wallet has walletAddress
          // set on their token too, so that check alone can't tell an
          // email session apart from a wallet session once a wallet is
          // on file, which is exactly the case this whole check exists
          // for.
          isExistingEmailSession = stillValid && (decoded.authMethod !== 'wallet' || !sessionWallet);
        } catch (e) {
          // fall through to a fresh sign-in challenge below
        }
      }

      // If this email-session user already has a DIFFERENT wallet
      // registered on their profile than whatever MetaMask account is
      // now active, stop here completely — don't even ask for a
      // signature. The property/investment page's own wallet-mismatch
      // check (Detail.js) already tells the user to switch accounts, so
      // popping a MetaMask "sign this message" prompt here on top of
      // that (which happens purely as a side effect of the account
      // switch itself, not anything the user asked for) reads as if
      // connecting the wrong wallet is doing *something* — attaching,
      // switching, logging in — when the correct behavior is that it
      // should do nothing at all. The backend (POST /api/user/wallet)
      // also independently refuses to overwrite an already-registered
      // wallet, but that check happening only *after* the user has
      // already signed is exactly the confusing sequence being fixed
      // here — the signature prompt itself should never appear.
      if (isExistingEmailSession) {
        const authState = store.getState().auth || {};
        const profile = authState.userprofile || {};
        const registeredWallet = web3data.family === "solana" ? profile.solanaWalletAddress : profile.walletAddress;
        // Already linked (this wallet: nothing to do, so no signature prompt on every reload; a different
        // wallet: stay out of the way, as before).
        if (registeredWallet) return;
      }

      // See runWalletAuthOnce in utils/walletAuth.js: this same onConnect
      // can be invoked concurrently by the mount effect above, by the
      // accountsChanged listener below (which fires as a side effect of
      // an explicit "Connect Wallet" click elsewhere), and by that
      // explicit click handler itself (web3modal/index.js). The lock
      // ensures only one nonce -> sign -> submit sequence for this wallet
      // is ever in flight — a losing concurrent caller just backs off
      // instead of racing a second signature request that's guaranteed to
      // fail verification.
      await runWalletAuthOnce(async () => {
        let signature;
        try {
          signature = await signWalletLoginChallenge(web3data.signer, web3data.address);
        } catch (sigErr) {
          return;
        }

        if (isExistingEmailSession) {
          try {
            const attachResult = await store.dispatch(attachWallet(web3data.address, signature));
            if (!attachResult.success) {
            }
          } catch (attachErr) {
          }
          return;
        }

        const loginData = { walletAddress: web3data.address, signature };
        store.dispatch(loginUserViaWallet(loginData));
      });
    }
  };

  // Solana wallets (Phantom...) report account changes through their own events, not
  // window.ethereum. Same behaviour as the EVM listener below: re-run the sign-in for a
  // wallet session, stay out of the way of an email session, and drop the connection
  // when the wallet disconnects.
  useEffect(() => {
    return onSolanaAccountChanged(async (address) => {
      try {
        if (!address) {
          if (store.getState().web3.family === "solana") store.dispatch(clearWeb3());
          return;
        }
        if (isEmailSessionActive()) return;
        if (store.getState().web3.family !== "solana") return;
        await onConnect();
      } catch (e) {}
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Listen for MetaMask account and chain changes to auto re-auth
  useEffect(() => {
    const eth = typeof window !== "undefined" ? window.ethereum : null;
    if (!eth || !eth.on) return;
    // Wallet / network events use the shared toast (audit 4.1)
    const toast = (text) => Notify.info(text);
    const handleAccountsChanged = async (accounts) => {
      try {
        if (accounts && accounts.length > 0) {
          // Skip entirely (no picker, no toast, no reconnect) for an
          // email-session user — see isEmailSessionActive() above for
          // why. Their wallet connection is handled exclusively by the
          // explicit buttons on the Profile and property/investment
          // pages, never by this background listener.
          if (isEmailSessionActive()) {
            return;
          }
          toast("Wallet account changed");
          await onConnect();
        }
      } catch (e) {}
    };
    const handleChainChanged = async (_chainId) => {
      try {
        const newChainId = toChainId(_chainId);
        const chain = getChain(newChainId);

        // Wallet moved to a network we don't support: flag it and tell the
        // user, but do NOT push them back — they stay where they chose.
        if (!chain || !isSupportedChain(newChainId)) {
          store.dispatch(setUnsupportedChain(newChainId));
          Notify.warning("Unsupported network — choose XDC or Avalanche");
          return;
        }

        // Supported network: the wallet is the source of truth. Switch the
        // active chain, then rebuild the provider/signer/contract instances
        // from THAT chain's addresses.
        setActiveChainId(newChainId);
        toast("Network changed to " + chain.shortName);

        if (isEmailSessionActive()) {
          // Email-session users never get background re-auth; just refresh
          // the web3 state if their wallet is already connected.
          if (store.getState().web3.connected) {
            const web3data = await onWalletConnect();
            if (web3data) store.dispatch(setWeb3(web3data));
          }
          return;
        }
        await onConnect();
      } catch (e) {}
    };
    eth.on("accountsChanged", handleAccountsChanged);
    eth.on("chainChanged", handleChainChanged);
    return () => {
      try {
        eth.removeListener("accountsChanged", handleAccountsChanged);
        eth.removeListener("chainChanged", handleChainChanged);
      } catch (e) {}
    };
  }, []);

  // Don't render routes until auth bootstrap completes
  if (!bootstrapped) {
    return <Loader />;
  }

  return (
    <LoadingProvider>
      <BrowserRouter>
        <ScrollToTop />
        <MotionLayer />
        <DialogHost />
        {connected && authLoading && !isAuthenticated && (
          <FullPageLoader message="Connecting your wallet" />
        )}
        <Suspense fallback={<Loader />}>
          <Routes>
            {/* ---------- Public Routes ---------- */}
            <Route index element={<Landing />} />
            <Route path="/comingsoon" element={<ComingSoon />} />
            <Route path="/about" element={<AboutPage />} />
            <Route path="/contact" element={<Contact />} />
            <Route path="/terms" element={<Terms />} />
            <Route path="/cookies" element={<Cookies />} />
            <Route path="/privacypolicy" element={<PrivacyPolicy />} />
            <Route path="/generalrisks" element={<GeneralRisks />} />
            <Route path="/properties" element={<Property />} />
            <Route path="/property-details/:id" element={<PropertyDetail />} />
            <Route path="/login" element={<Login />} />
            <Route path="/register" element={<Register />} />
            <Route path="/verifyotp" element={<VerifyOtp />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/verifyaccount" element={<VerifyAccount />} />

            {/* ---------- Admin Routes ---------- */}
            <Route path="/admin" element={<AdminRoute />}>
              <Route element={<AdminLayout />}>
                {/* /admin on its own used to render an empty layout (blank page) */}
                <Route index element={<Navigate to="/admin/kyc" replace />} />
                <Route path="kyc" element={<AdminKyc />} />
                <Route path="properties" element={<AdminProperties />} />
                <Route path="escrow" element={<AdminEscrow />} />
                <Route path="payouts" element={<AdminPayouts />} />
                <Route path="facility-managers" element={<AdminFacilityManagers />} />
                <Route path="rates" element={<AdminRates />} />
              </Route>
              <Route path="logout" element={<Logout />} />
            </Route>

            {/* ---------- Facility Manager Routes ---------- */}
            {/* Standalone role/panel — deliberately not nested under
                /admin. See FacilityManagerRoute.js: a facilityManager who
                tries to visit /admin/* is bounced to /login just like
                anyone else who isn't an admin, and vice versa. */}
            <Route path="/facility-manager" element={<FacilityManagerRoute />}>
              <Route element={<FacilityManagerLayout />}>
                <Route path="payouts" element={<FacilityManagerPayouts />} />
                <Route index element={<FacilityManagerPayouts />} />
              </Route>
              <Route path="logout" element={<Logout />} />
            </Route>

            {/* ---------- Protected Routes ---------- */}
            <Route path="/user" element={<PrivateRoute />}>
              <Route path="dashboard" element={<Dashboard />} />
              <Route path="marketplace/:propertyId" element={<Marketplace />} />
              <Route path="governance/:propertyId" element={<Governance />} />
              <Route path="distributions" element={<TokenDistributionPage />} />
              <Route
                path="distributions/:id"
                element={<TokenDistributionDetailPage />}
              />
              <Route path="holdings" element={<TokenHoldingsPage />} />
              <Route path="spv" element={<Spv />} />
              <Route path="spv/add" element={<AddSpv />} />
              <Route path="kyc" element={<Kyc />} />
              <Route path="wishlist" element={<Wishlist />} />
              <Route path="myproperty" element={<MyProperty />} />
              <Route path="addproperty" element={<CreateProperty />} />
              <Route path="editproperty/:id" element={<EditProperty />} />
              <Route path="buyers" element={<Buyers />} />
              <Route
                path="buyers/digital-certificate"
                element={<DigitalCertificate />}
              />
              <Route path="mypurchases" element={<MyPurchases />} />
              <Route path="profile" element={<Profile />} />
              <Route path="add-email-login" element={<AttachEmailLogin />} />
              <Route path="updatepassword" element={<UpdatePassword />} />
              <Route path="logout" element={<Logout />} />
              <Route path="membership" element={<PackageDetail />} />
              <Route path="complete-profile" element={<ConfirmProfile />} />
              <Route
                path="valuation-partners"
                element={<ValuationPartners />}
              />
              <Route path="fully-funded" element={<FullyFunded />} />
              <Route path="refunds" element={<Refunds />} />
              <Route path="user-guide" element={<UserGuide />} />
            </Route>
          </Routes>
        </Suspense>
      </BrowserRouter>
    </LoadingProvider>
  );
}